import { useEffect, useState } from 'react';

/**
 * Custom hook to detect if an element's content is overflowing its container.
 * Useful for conditionally showing "See more" buttons on clamped content.
 * Checks both vertical overflow (e.g. line-clamp) and horizontal (e.g. truncate).
 *
 * @param ref - Reference to the DOM element to check
 * @param isEnabled - Whether the overflow check should be active
 * @returns boolean indicating if the content is overflowing
 */
export function useIsOverflowing(
  ref: React.RefObject<HTMLElement>,
  isEnabled: boolean,
) {
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (!isEnabled || !ref.current) {
      setIsOverflowing(false);
      return undefined;
    }

    const checkOverflow = () => {
      if (ref.current) {
        const el = ref.current;
        const hasOverflow =
          el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
        setIsOverflowing(hasOverflow);
      }
    };

    // Check immediately
    checkOverflow();

    // Watch for resize events (e.g., window resize, dynamic content changes)
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(ref.current);

    function cleanup() {
      resizeObserver.disconnect();
    }
    return cleanup;
  }, [ref, isEnabled]);

  return isOverflowing;
}
